import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, MapPin, ChevronRight, Circle, Shield, Users } from 'lucide-react';
// 🛰️ Using unified types to ensure system-wide consistency
import { CalendarEvent } from '@/types';
import { isToday, isTomorrow, format, parseISO, isSameDay } from 'date-fns';

interface CalendarAgendaProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
  // 🛡️ Hybrid Scope Props
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
}

/**
 * 🗓️ CalendarAgenda Widget
 * Refactored for Hybrid Scoping: Merges Personal and Shared streams with source attribution.
 */
export const CalendarAgenda: React.FC<CalendarAgendaProps> = ({ 
  events, 
  onEventClick,
  currentHouseholdId,
  activeScope
}) => {
  const { t } = useTranslation();

  // 🛡️ HYBRID FILTERING: Deterministic chronological order across partitions
  const sortedEvents = useMemo(() => {
    return [...(events || [])]
      .filter(event => {
        const source = (event as any).source || 'personal';
        // If not in a household, strictly silo to personal partition
        if (!currentHouseholdId) return source === 'personal';
        // If in a household, respect the toggle
        if (activeScope === 'PERSONAL') return source === 'personal';
        if (activeScope === 'SHARED') return source === 'household';
        return true; // BOTH
      })
      .sort((a: CalendarEvent, b: CalendarEvent) => 
        parseISO(a.start).getTime() - parseISO(b.start).getTime()
      )
      .slice(0, 5);
  }, [events, currentHouseholdId, activeScope]);

  if (sortedEvents.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-3">
        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-full">
          <Calendar className="w-8 h-8 text-slate-200" />
        </div>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
          {t('dash.calendar.empty', 'Clear Horizon')}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-2 overflow-y-auto no-scrollbar">
      <div className="space-y-6">
        {sortedEvents.map((event: CalendarEvent, idx: number) => {
          const startDate = parseISO(event.start);
          const prevEvent = sortedEvents[idx - 1];
          const source = (event as any).source || 'personal';
          
          const showDateHeader = idx === 0 || (prevEvent && !isSameDay(startDate, parseISO(prevEvent.start)));

          return (
            <div key={event.id} className="relative">
              {/* --- DATE SLICER --- */}
              {showDateHeader && (
                <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 pb-2 mb-2 flex items-center justify-between">
                  <span className="text-[9px] font-black uppercase tracking-[0.3em] text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-full">
                    {isToday(startDate) ? t('common.today', 'Today') : 
                     isTomorrow(startDate) ? t('common.tomorrow', 'Tomorrow') : 
                     format(startDate, 'EEEE d')}
                  </span>
                </div>
              )}

              {/* --- EVENT CARD --- */}
              <button
                onClick={() => onEventClick(event)}
                className="w-full flex items-start gap-4 p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all group border border-transparent hover:border-slate-100 dark:hover:border-slate-700"
              >
                {/* Time Strip & Source Icon */}
                <div className="flex flex-col items-end min-w-[55px] pt-1">
                  <span className="text-xs font-black text-slate-900 dark:text-white tabular-nums">
                    {format(startDate, 'HH:mm')}
                  </span>
                  <div className="mt-2 relative">
                    <Circle className={`w-2 h-2 fill-current ${event.color || 'text-indigo-500'}`} />
                    {/* 🛡️ Source Attribution Badge */}
                    <div className="absolute -right-3 -top-1">
                      {source === 'personal' ? (
                        <Shield className="w-2 h-2 text-emerald-500/50" />
                      ) : (
                        <Users className="w-2 h-2 text-indigo-500/50" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Info Block */}
                <div className="flex-1 text-left space-y-1">
                  <div className="flex items-center gap-2">
                    <h4 className={`text-[11px] font-black uppercase tracking-tight leading-tight italic ${source === 'personal' ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-800 dark:text-slate-100'}`}>
                      {event.title}
                    </h4>
                  </div>
                  <div className="flex flex-col gap-1.5 mt-1">
                    <div className="flex items-center gap-1.5 opacity-50">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-[8px] font-black text-slate-500 uppercase">
                        {Math.round((parseISO(event.end).getTime() - startDate.getTime()) / 60000)}m 
                        {source === 'personal' && <span className="ml-1 text-emerald-500">• Private</span>}
                      </span>
                    </div>
                    {event.location?.address && (
                      <div className="flex items-center gap-1.5 opacity-50">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        <span className="text-[8px] font-black text-slate-500 truncate max-w-[130px] uppercase tracking-tighter">
                          {event.location.address}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <ChevronRight className="w-4 h-4 text-slate-200 group-hover:text-indigo-500 mt-1 transition-colors" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};